import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import {
  RightSidebarContext,
  useRightSidebar,
  type MainAreaView,
} from '../contexts/RightSidebarContext';
import {
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
} from './ui/sidebar';
import {
  TooltipContent,
  ButtonTooltip,
} from './ui/tooltip';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { PanelRightOpen, PanelRightClose, Lightbulb, MessageSquare, Bot, FileText, Download, GripVertical, Settings, HelpCircle, BarChart3 } from 'lucide-react';
import { CONCEPT_ICONS } from '../utils/concept-icons';

const ShareIcon = CONCEPT_ICONS.share;
const VersionHistoryIcon = CONCEPT_ICONS.version_history;
import { SuggestionsPanelHeaderActions } from './SuggestionsPanelHeaderActions';
import { cn } from '@/lib/utils';
import {
  getFeedbackAnalyzeDisableReason,
  getFeedbackAnalyzeDisableTooltip,
} from '../utils/feedback-analyze-availability';
import type { ChatSelectionContext, OpenChatOptions } from '../utils/chat-text-context';
import { Chapters } from './Chapters';
import { Storylines } from './Storylines';
import { Scenes } from './Scenes';
import { Characters } from './Characters';
import { Locations } from './Locations';
import { Comments } from './Comments';
import type { MentionableEntity } from './RichTextField';
import { DocumentChat } from './DocumentChat';
import { TechnicalSheet } from './TechnicalSheet';
import { ExportDocument } from './ExportDocument';
import { ShareDocument } from './ShareDocument';
import { VersionHistory } from './VersionHistory';
import { DocumentStats } from './DocumentStats';
import { TooltipProvider } from './ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Editor } from '@tiptap/core';
import { SuggestionCard, isTemporarySuggestionId } from './SuggestionCard';
import { CountBadge } from './CountBadge';
import { sortPendingSuggestions, sortPendingSuggestionsByDocumentOrder } from '../utils/editor-utils';
import {
  getNextSuggestionsPanelVisibility,
  type SuggestionsPanelIntent,
} from '../utils/suggestions-panel-visibility';
import { Separator } from './ui/separator';
import { WritingSettings } from './WritingSettings';
import { EditorHelpDialog } from './EditorHelpDialog';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { API_BASE } from '../config';
import { findTocHeadingPosition } from '../utils/document-section-navigation';

const RIGHT_SIDEBAR_COOKIE_NAME = 'right_sidebar_state';
const RIGHT_SIDEBAR_DOCUMENT_COOKIE_NAME = 'right_sidebar_document_id';
const RIGHT_SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const RIGHT_SIDEBAR_WIDTH = '20rem';
const RIGHT_SIDEBAR_WIDTH_MOBILE = '14rem';
const RIGHT_SIDEBAR_WIDTH_ICON = '3.5rem';
const RIGHT_SIDEBAR_WIDTH_ICON_EXPANDED = '12rem';
const RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME = 'right_sidebar_icon_expanded';
const RIGHT_SIDEBAR_WIDTH_COOKIE_NAME = 'right_sidebar_width';
const MIN_DRAWER_WIDTH = 500;
const MAX_DRAWER_WIDTH = 1200;

type RightSidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean | ((value: boolean) => boolean)) => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  toggleSidebar: () => void;
};

const RightSidebarShadcnContext = createContext<RightSidebarContextProps | null>(null);

const useRightSidebarShadcn = (): RightSidebarContextProps => {
  const context = useContext(RightSidebarShadcnContext);
  if (!context) {
    throw new Error('useRightSidebarShadcn must be used within RightSidebarProviderWrapper');
  }
  return context;
};

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 rounded-md px-4 py-5 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:!justify-center group-data-[collapsible=icon]:!text-center [&>span:last-child]:truncate [&>svg]:size-5 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

// Matches the project menu's expanded item rows (see ProjectExplorer's
// SidebarMenuSubButton: h-[38px], gap-2, px-2, 16px icons, text-primary label).
const expandedRailButtonClassName = 'h-[38px] gap-2 px-2 justify-start';
const expandedRailIconClassName = 'h-4 w-4 shrink-0 text-secondary-foreground';
const expandedRailLabelClassName = 'truncate text-primary';

const RightSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
    hideTooltip?: boolean;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      tooltip,
      hideTooltip = false,
      className,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useRightSidebarShadcn();

    const button = (
      <Comp
        ref={ref}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
        {...props}
      />
    );

    return (
      <ButtonTooltip tooltip={tooltip} side="right" hidden={isMobile || hideTooltip}>
        {button}
      </ButtonTooltip>
    );
  }
);
RightSidebarMenuButton.displayName = 'RightSidebarMenuButton';

type Suggestion = {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  originalText: string;
  suggestionText: string;
  issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
  feedbackText: string;
  llmCallId: string | null;
  llmCall?: {
    id: string;
    userId: string | null;
    documentId: string | null;
    triggerSource: string;
    provider: 'gemini' | 'openai';
    model: string;
    prompt: string;
    response: string | null;
    promptTokens: number | null;
    responseTokens: number | null;
    costUsd: number | null;
    createdAt: string;
  } | null;
  languageCode: string | null;
  languageName: string | null;
  shownAt: string;
  status: 'pending' | 'applied' | 'rejected' | 'reported';
  closedAt: string | null;
  createdAt: string;
};

const RightSidebarProviderWrapper: React.FC<{ children: React.ReactNode; defaultOpen?: boolean }> = ({ children, defaultOpen = false }) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof document === 'undefined') return defaultOpen;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => c.trim().startsWith(`${RIGHT_SIDEBAR_COOKIE_NAME}=`));
    if (cookie) {
      const value = cookie.split('=')[1];
      return value === 'true';
    }
    return defaultOpen;
  });

  const handleSetOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      setOpen(openState);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    },
    [open]
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile
      ? setOpenMobile((prev) => !prev)
      : handleSetOpen((prev) => !prev);
  }, [isMobile, handleSetOpen]);

  const state: 'expanded' | 'collapsed' = open ? 'expanded' : 'collapsed';

  const contextValue: RightSidebarContextProps = React.useMemo(
    () => ({
      state,
      open,
      setOpen: handleSetOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, handleSetOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <RightSidebarShadcnContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          style={
            {
              '--sidebar-width': RIGHT_SIDEBAR_WIDTH,
              '--sidebar-width-icon': RIGHT_SIDEBAR_WIDTH_ICON,
            } as React.CSSProperties
          }
          className="group/sidebar-wrapper flex min-h-svh w-full"
        >
          {children}
        </div>
      </TooltipProvider>
    </RightSidebarShadcnContext.Provider>
  );
};

type RightSidebarContextProviderProps = {
  children: React.ReactNode;
  activeMainAreaView?: MainAreaView;
  onSelectMainAreaView?: (view: MainAreaView) => void;
};

const RightSidebarContextProvider: React.FC<RightSidebarContextProviderProps> = ({ children, activeMainAreaView = null, onSelectMainAreaView }) => {
  const sidebar = useRightSidebarShadcn();
  const [activeView, setActiveView] = useState<'suggestions' | 'comments' | 'chat' | 'chapters' | 'analysis' | 'storylines' | 'scenes' | 'characters' | 'locations' | 'technicalSheet' | 'export' | 'share' | 'versionHistory' | 'stats' | 'settings' | null>(null);
  const [chatNavigateThreadId, setChatNavigateThreadId] = useState<string | null>(null);
  const [chatNavigateMessageId, setChatNavigateMessageId] = useState<string | null>(null);
  const [chatFocusRequest, setChatFocusRequest] = useState(0);
  const [chatSelectionContext, setChatSelectionContext] = useState<ChatSelectionContext | null>(null);

  const clearChatSelection = React.useCallback((): void => {
    setChatSelectionContext(null);
  }, []);

  const requestChatInputFocus = (): void => {
    setChatFocusRequest((current) => current + 1);
  };

  const toggleMainAreaView = (view: MainAreaView): void => {
    if (!onSelectMainAreaView) {
      return;
    }
    const nextView = activeMainAreaView === view ? null : view;
    onSelectMainAreaView(nextView);
    setActiveView(null);
    if (sidebar.isMobile) {
      sidebar.setOpenMobile(false);
    } else {
      sidebar.setOpen(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_COOKIE_NAME}=false; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    }
  };

  const applySuggestionsPanelVisibility = (intent: SuggestionsPanelIntent): void => {
    const next = getNextSuggestionsPanelVisibility(intent, {
      isMobile: Boolean(sidebar.isMobile && onSelectMainAreaView),
      activeMainAreaView,
      activeView,
    });

    if (next.activeMainAreaView !== activeMainAreaView && onSelectMainAreaView) {
      onSelectMainAreaView(next.activeMainAreaView as MainAreaView);
    }
    if (next.activeView !== activeView) {
      setActiveView(next.activeView as typeof activeView);
    }
    if (next.shouldOpenDesktopSidebar && !sidebar.open) {
      sidebar.setOpen(true);
    }
    if (next.shouldCloseMobileSidebar) {
      sidebar.setOpenMobile(false);
    }
  };

  const showSuggestions = (): void => {
    applySuggestionsPanelVisibility('toggle');
  };

  const openSuggestions = (): void => {
    applySuggestionsPanelVisibility('open');
  };

  const showComments = (): void => {
    if (sidebar.isMobile && onSelectMainAreaView) {
      toggleMainAreaView('comments');
      return;
    }
    if (activeMainAreaView && onSelectMainAreaView) {
      onSelectMainAreaView(null);
    }
    if (activeView === 'comments') {
      setActiveView(null);
    } else {
      if (!sidebar.open) {
        sidebar.setOpen(true);
      }
      setActiveView('comments');
    }
  };

  const openComments = (): void => {
    if (sidebar.isMobile && onSelectMainAreaView) {
      if (activeMainAreaView !== 'comments') {
        onSelectMainAreaView('comments');
      }
      sidebar.setOpenMobile(false);
      return;
    }
    if (activeMainAreaView && onSelectMainAreaView) {
      onSelectMainAreaView(null);
    }
    if (!sidebar.open) {
      sidebar.setOpen(true);
    }
    setActiveView('comments');
  };

  const showChat = (): void => {
    if (sidebar.isMobile && onSelectMainAreaView) {
      toggleMainAreaView('chat');
      return;
    }
    if (activeMainAreaView && onSelectMainAreaView) {
      onSelectMainAreaView(null);
    }
    if (activeView === 'chat') {
      setActiveView(null);
    } else {
      if (!sidebar.open) {
        sidebar.setOpen(true);
      }
      setActiveView('chat');
    }
  };

  const openChat = (options?: OpenChatOptions): void => {
    if (options && Object.prototype.hasOwnProperty.call(options, 'selection')) {
      setChatSelectionContext(options.selection ?? null);
    }
    if (sidebar.isMobile && onSelectMainAreaView) {
      if (activeMainAreaView !== 'chat') {
        onSelectMainAreaView('chat');
      }
      requestChatInputFocus();
      return;
    }
    if (activeMainAreaView && onSelectMainAreaView) {
      onSelectMainAreaView(null);
    }
    if (!sidebar.open) {
      sidebar.setOpen(true);
    }
    setActiveView('chat');
    requestChatInputFocus();
  };

  const navigateToChat = (threadId: string, messageId?: string): void => {
    openChat();
    setChatNavigateThreadId(threadId);
    setChatNavigateMessageId(messageId ?? null);
  };

  const clearChatNavigation = (): void => {
    setChatNavigateThreadId(null);
    setChatNavigateMessageId(null);
  };

  const showChapters = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('chapters');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'chapters') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('chapters');
      }
    } else {
      if (activeView === 'chapters') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('chapters');
      }
    }
  };

  const showAnalysis = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('analysis');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'analysis') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('analysis');
      }
    } else {
      if (activeView === 'analysis') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('analysis');
      }
    }
  };

  const showStorylines = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('storylines');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'storylines') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('storylines');
      }
    } else {
      if (activeView === 'storylines') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('storylines');
      }
    }
  };

  const showScenes = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('scenes');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'scenes') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('scenes');
      }
    } else {
      if (activeView === 'scenes') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('scenes');
      }
    }
  };

  const showCharacters = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('characters');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'characters') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('characters');
      }
    } else {
      if (activeView === 'characters') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('characters');
      }
    }
  };

  const showLocations = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('locations');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'locations') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('locations');
      }
    } else {
      if (activeView === 'locations') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('locations');
      }
    }
  };

  const showTechnicalSheet = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('technicalSheet');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'technicalSheet') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('technicalSheet');
      }
    } else {
      if (activeView === 'technicalSheet') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('technicalSheet');
      }
    }
  };

  const showExport = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('export');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'export') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('export');
      }
    } else {
      if (activeView === 'export') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('export');
      }
    }
  };

  const showShare = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('share');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'share') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('share');
      }
    } else {
      if (activeView === 'share') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('share');
      }
    }
  };

  const showVersionHistory = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('versionHistory');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'versionHistory') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('versionHistory');
      }
    } else {
      if (activeView === 'versionHistory') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('versionHistory');
      }
    }
  };

  const showStats = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('stats');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'stats') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('stats');
      }
    } else {
      if (activeView === 'stats') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('stats');
      }
    }
  };

  const showSettings = (): void => {
    if (onSelectMainAreaView) {
      toggleMainAreaView('settings');
      return;
    }
    if (sidebar.isMobile) {
      if (sidebar.openMobile && activeView === 'settings') {
        sidebar.setOpenMobile(false);
        setActiveView(null);
      } else {
        if (!sidebar.openMobile) {
          sidebar.setOpenMobile(true);
        }
        setActiveView('settings');
      }
    } else {
      if (activeView === 'settings') {
        setActiveView(null);
      } else {
        if (!sidebar.open) {
          sidebar.setOpen(true);
        }
        setActiveView('settings');
      }
    }
  };

  const collapseSidebar = (): void => {
    setActiveView(null);
    if (sidebar.isMobile) {
      sidebar.setOpenMobile(false);
    } else {
      sidebar.setOpen(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_COOKIE_NAME}=false; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    }
  };

  const closeChat = (): void => {
    if (activeMainAreaView === 'chat' && onSelectMainAreaView) {
      onSelectMainAreaView(null);
      return;
    }
    if (activeView === 'chat') {
      collapseSidebar();
    }
  };

  const toggleSidebar = (): void => {
    sidebar.toggleSidebar();
  };

  return (
    <RightSidebarContext.Provider
      value={{
        toggleSidebar,
        open: sidebar.open,
        openMobile: sidebar.openMobile,
        setOpenMobile: sidebar.setOpenMobile,
        showSuggestions,
        openSuggestions,
        showComments,
        openComments,
        showChat,
        openChat,
        chatSelectionContext,
        clearChatSelection,
        showChapters,
        showAnalysis,
        showStorylines,
        showScenes,
        showCharacters,
        showLocations,
        showTechnicalSheet,
        showExport,
        showShare,
        showVersionHistory,
        showStats,
        showSettings,
        navigateToChat,
        chatNavigateThreadId,
        chatNavigateMessageId,
        chatFocusRequest,
        clearChatNavigation,
        activeView,
        activeMainAreaView,
        collapseSidebar,
        closeChat,
      }}
    >
      {children}
    </RightSidebarContext.Provider>
  );
};

type EditorRightSidebarProviderProps = {
  children: React.ReactNode;
  activeMainAreaView?: MainAreaView;
  onSelectMainAreaView?: (view: MainAreaView) => void;
};

export const EditorRightSidebarProvider: React.FC<EditorRightSidebarProviderProps> = ({ children, activeMainAreaView = null, onSelectMainAreaView }) => {
  return (
    <RightSidebarProviderWrapper>
      <RightSidebarContextProvider activeMainAreaView={activeMainAreaView} onSelectMainAreaView={onSelectMainAreaView}>
        {children}
      </RightSidebarContextProvider>
    </RightSidebarProviderWrapper>
  );
};

type TocHeading = {
  level: number;
  text: string;
  pos: number;
  kind?: 'heading' | 'scene';
};

type TocParagraph = {
  text: string;
  pos: number;
};

type EditorRightSidebarProps = {
  editor?: Editor | null;
  suggestions?: Suggestion[];
  onLoadSuggestions?: (options?: { sortByDocumentOrder?: boolean }) => void | Promise<void>;
  onTriggerAnalysis?: () => void;
  isAnalyzing?: boolean;
  onApplySuggestion?: (suggestion: Suggestion) => void;
  onRejectSuggestion?: (suggestion: Suggestion) => void;
  onDismissSuggestion?: (suggestion: Suggestion) => void;
  onDismissAllSuggestions?: () => void | Promise<void>;
  onHighlightSuggestions?: () => void;
  onClearSuggestionHighlights?: () => void;
  onScrollToSuggestion?: (suggestion: Suggestion) => void;
  isLoadingSuggestions?: boolean;
  tocHeadings?: TocHeading[];
  tocParagraphs?: TocParagraph[];
  showParagraphs?: boolean;
  onToggleShowParagraphs?: (show: boolean) => void;
  onScrollToTocPosition?: (pos: number) => void;
  onMoveTocSection?: (fromIndex: number, toIndex: number) => void;
  draggedTocIndex?: number | null;
  setDraggedTocIndex?: (index: number | null) => void;
  dragOverTocIndex?: number | null;
  setDragOverTocIndex?: (index: number | null) => void;
  touchDraggedIndex?: number | null;
  setTouchDraggedIndex?: (index: number | null) => void;
  touchDragOverIndex?: number | null;
  setTouchDragOverIndex?: (index: number | null) => void;
  touchStartY?: number | null;
  setTouchStartY?: (y: number | null) => void;
  tocListRef?: React.RefObject<HTMLUListElement>;
  isReadOnly?: boolean;
  projectId?: string | null;
  documentId?: string | null;
  shareToken?: string | null;
  token?: string | null;
  showErrorToast?: (message: string) => void;
  showToast?: (message: string) => void;
  t?: (key: string) => string;
  ydoc?: any;
  documentTitle?: string;
  projectTitle?: string;
  documentProjectId?: string | null;
  wordCount?: number;
  readingTime?: number;
  pageCount?: number;
  selectedWordCount?: number;
  selectedReadingTime?: number;
  selectedPageCount?: number;
  onRegisterShowSuggestions?: (showSuggestions: () => void) => void;
  onRegisterShowComments?: (showComments: () => void) => void;
  onRegisterRefreshComments?: (refreshComments: () => void) => void;
  isFocusMode?: boolean;
  comments?: Array<{
    id: string;
    documentId: string;
    userId: string;
    userEmail?: string;
    content: string;
    fromPos: number;
    toPos: number;
    selectedText: string;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string | null;
    replies?: Array<{
      id: string;
      userId: string;
      userEmail?: string;
      createdAt: string;
      comment: string;
    }>;
  }>;
  onLoadComments?: () => Promise<void>;
  documentOwnerId?: string | null;
  canManageSharing?: boolean;
  mentionableEntities?: MentionableEntity[];
};

export const EditorRightSidebar: React.FC<EditorRightSidebarProps> = ({
  editor = null,
  suggestions = [],
  onLoadSuggestions,
  onTriggerAnalysis,
  isAnalyzing = false,
  onApplySuggestion,
  onRejectSuggestion,
  onDismissSuggestion,
  onDismissAllSuggestions,
  onHighlightSuggestions,
  onClearSuggestionHighlights,
  onScrollToSuggestion,
  isLoadingSuggestions = false,
  tocHeadings = [],
  tocParagraphs = [],
  showParagraphs = false,
  onToggleShowParagraphs,
  onScrollToTocPosition,
  onMoveTocSection,
  draggedTocIndex = null,
  setDraggedTocIndex,
  dragOverTocIndex = null,
  setDragOverTocIndex,
  touchDraggedIndex = null,
  setTouchDraggedIndex,
  touchDragOverIndex = null,
  setTouchDragOverIndex,
  touchStartY = null,
  setTouchStartY,
  tocListRef,
  isReadOnly = false,
  projectId,
  documentId,
  shareToken = null,
  token = null,
  showErrorToast = () => {},
  showToast = () => {},
  t = (key: string) => key,
  ydoc = null,
  documentTitle = '',
  projectTitle = '',
  documentProjectId = null,
  wordCount = 0,
  readingTime = 0,
  pageCount = 0,
  selectedWordCount = 0,
  selectedReadingTime = 0,
  selectedPageCount = 0,
  onRegisterShowSuggestions,
  onRegisterShowComments,
  onRegisterRefreshComments,
  isFocusMode = false,
  comments = [],
  onLoadComments,
  documentOwnerId = null,
  canManageSharing = false,
  mentionableEntities = [],
}) => {
  const { toggleSidebar, open, openMobile, setOpenMobile, activeView, activeMainAreaView, showSuggestions, openSuggestions, showComments, openComments, showChat, showTechnicalSheet, showExport, showShare, showVersionHistory, showStats, showSettings, collapseSidebar, navigateToChat, chatNavigateThreadId, chatNavigateMessageId, chatFocusRequest, clearChatNavigation, chatSelectionContext, clearChatSelection } = useRightSidebar();
  const { user, token: authToken } = useAuth();
  const isOwner = documentOwnerId && user?.id === documentOwnerId;
  const canShare = Boolean(canManageSharing || isOwner);
  const pendingSuggestionsCount = sortPendingSuggestions(suggestions).length;
  const pendingCommentsCount = comments.filter((comment) => !comment.deletedAt).length;
  const [commentsRefreshTrigger, setCommentsRefreshTrigger] = useState<number>(0);
  const [deletedComments, setDeletedComments] = useState<typeof comments>([]);
  const [commentsTab, setCommentsTab] = useState<'pending' | 'deleted'>('pending');
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  useEffect(() => {
    if (onRegisterShowSuggestions) {
      onRegisterShowSuggestions(openSuggestions);
    }
  }, [onRegisterShowSuggestions, openSuggestions]);

  useEffect(() => {
    if (onRegisterShowComments) {
      onRegisterShowComments(openComments);
    }
  }, [onRegisterShowComments, openComments]);

  const refreshComments = React.useCallback((): void => {
    setCommentsRefreshTrigger((prev) => prev + 1);
  }, []);

  const scrollToDocumentSection = useCallback((title: string, level: number): void => {
    const pos = findTocHeadingPosition(tocHeadings, title, level);
    if (pos !== null && onScrollToTocPosition) {
      onScrollToTocPosition(pos);
    }
  }, [onScrollToTocPosition, tocHeadings]);

  const loadDeletedComments = React.useCallback(async (): Promise<void> => {
    if (!authToken || !documentId || documentId === 'new') {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/comments?includeDeleted=true`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDeletedComments(data.comments || []);
      } else {
        console.error('Failed to load deleted comments');
      }
    } catch (err) {
      console.error('Error loading deleted comments:', err);
    }
  }, [authToken, documentId]);

  const syncCommentLists = React.useCallback(async (): Promise<void> => {
    if (onLoadComments) {
      await onLoadComments();
    }
    await loadDeletedComments();
  }, [onLoadComments, loadDeletedComments]);

  useEffect(() => {
    if (onRegisterRefreshComments) {
      onRegisterRefreshComments(refreshComments);
    }
  }, [onRegisterRefreshComments, refreshComments]);

  useEffect(() => {
    if (activeView === 'comments' && commentsTab === 'deleted') {
      loadDeletedComments();
    }
  }, [activeView, commentsTab, loadDeletedComments]);

  const prevSuggestionsPanelOpenRef = useRef(false);

  useEffect(() => {
    const isSuggestionsPanelOpen = activeView === 'suggestions' || activeMainAreaView === 'suggestions';
    if (isSuggestionsPanelOpen && !prevSuggestionsPanelOpenRef.current && onLoadSuggestions) {
      void onLoadSuggestions({ sortByDocumentOrder: true });
    }
    prevSuggestionsPanelOpenRef.current = isSuggestionsPanelOpen;
  }, [activeView, activeMainAreaView, onLoadSuggestions]);

  useEffect(() => {
    if (activeView === 'suggestions' && onHighlightSuggestions && suggestions.length > 0) {
      const timeoutId = setTimeout(() => {
        onHighlightSuggestions();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [activeView, onHighlightSuggestions, suggestions]);

  const getIssueTypeLabel = (issueType: string): string => {
    const labels: Record<string, string> = {
      orthographic: t('orthographic'),
      preciseness: t('preciseness'),
      clarity: t('clarity'),
      consistency: t('consistency'),
      orthographic_consistency: t('orthographic_consistency'),
      grammar: t('grammar'),
      spelling: t('spelling'),
      punctuation: t('punctuation'),
      sentence_structure: t('sentence_structure'),
      repetition: t('repetition'),
      overused_words: t('overused_words'),
      cacophony: t('cacophony'),
      passive_voice: t('passive_voice'),
      show_vs_tell: t('show_vs_tell'),
      pacing: t('pacing'),
      adverbs: t('adverbs'),
      adjectives: t('adjectives'),
      cliches: t('cliches'),
      transitions: t('transitions'),
      style: t('style'),
      strength: t('strength'),
    };
    return labels[issueType] || issueType;
  };

  const renderSuggestions = (): React.ReactNode => {
    const pendingSuggestions = sortPendingSuggestionsByDocumentOrder(editor ?? null, suggestions);

    const analyzeDisableReason = onTriggerAnalysis
      ? getFeedbackAnalyzeDisableReason(editor, { allowDocumentFallback: true })
      : null;
    const isAnalyzeDisabled = analyzeDisableReason !== null;
    const analyzeTooltip = getFeedbackAnalyzeDisableTooltip(analyzeDisableReason, t);

    return (
      <div className="flex flex-col flex-1 min-h-0 h-full min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-4 flex-shrink-0">
          <h2 className="text-lg font-semibold">{t('suggestions')}</h2>
          <SuggestionsPanelHeaderActions
            pendingSuggestionsCount={pendingSuggestions.length}
            onDismissAll={onDismissAllSuggestions}
            isDismissAllDisabled={pendingSuggestions.some((suggestion) => isTemporarySuggestionId(suggestion.id))}
            dismissAllDisabledTooltip={t('dismiss_all_suggestions_unavailable_until_saved')}
            onTriggerAnalysis={onTriggerAnalysis}
            isAnalyzing={isAnalyzing}
            isAnalyzeDisabled={isAnalyzeDisabled}
            analyzeTooltip={analyzeTooltip}
            t={t}
          />
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingSuggestions ? (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              {t('loading')}
            </div>
          ) : pendingSuggestions.length === 0 ? (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              {t('no_suggestions')}
            </div>
          ) : (
            <div className="flex flex-col">
              {pendingSuggestions.map((suggestion, index) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onScrollToSuggestion={onScrollToSuggestion}
                  onApplySuggestion={onApplySuggestion}
                  onRejectSuggestion={onRejectSuggestion}
                  onDismissSuggestion={onDismissSuggestion}
                  onNavigateToChat={navigateToChat}
                  t={t}
                  getIssueTypeLabel={getIssueTypeLabel}
                  showDivider={index < pendingSuggestions.length - 1}
                  showOriginalAtTop={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChapters = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Chapters documentId={documentId} projectId={projectId || undefined} isSidebar={true} mode="outline" />;
  };

  const renderAnalysis = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Chapters documentId={documentId} projectId={projectId || undefined} isSidebar={true} mode="analysis" onScrollToSection={scrollToDocumentSection} />;
  };

  const renderStorylines = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Storylines documentId={documentId} projectId={projectId || undefined} isSidebar={true} />;
  };

  const renderScenes = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Scenes documentId={documentId} projectId={projectId || undefined} isSidebar={true} />;
  };

  const renderCharacters = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Characters documentId={documentId} projectId={projectId || undefined} isSidebar={true} />;
  };

  const renderLocations = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return <Locations documentId={documentId} projectId={projectId || undefined} isSidebar={true} />;
  };

  const renderComments = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return (
      <div className="flex flex-col h-full min-h-0">
        <Tabs value={commentsTab} onValueChange={(value) => setCommentsTab(value as 'pending' | 'deleted')} className="flex flex-col h-full min-h-0">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold m-0">{t('comments')}</h3>
            <TabsList className="h-7">
              <TabsTrigger value="pending" className="text-xs px-2 py-1 h-6">{t('pending')}</TabsTrigger>
              <TabsTrigger value="deleted" className="text-xs px-2 py-1 h-6">{t('comments_resolved_tab')}</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="pending" className="flex-1 overflow-y-auto min-h-0 mt-0">
            <Comments editor={editor} documentId={documentId} isReadOnly={isReadOnly} isVisible={activeView === 'comments' && commentsTab === 'pending'} refreshTrigger={commentsRefreshTrigger} isFocusMode={isFocusMode} comments={comments} onLoadComments={syncCommentLists} documentOwnerId={documentOwnerId} hideHeading={true} mentionableEntities={mentionableEntities} />
          </TabsContent>
          <TabsContent value="deleted" className="flex-1 overflow-y-auto min-h-0 mt-0">
            <Comments editor={editor} documentId={documentId} isReadOnly={isReadOnly} isVisible={activeView === 'comments' && commentsTab === 'deleted'} refreshTrigger={commentsRefreshTrigger} isFocusMode={isFocusMode} comments={deletedComments} onLoadComments={syncCommentLists} documentOwnerId={documentOwnerId} hideHeading={true} commentThreadList="resolved" mentionableEntities={mentionableEntities} />
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  const renderChat = (): React.ReactNode => {
    if (!editor) {
      return null;
    }

    const scrollToSuggestionById = (suggestionId: string): void => {
      const suggestion = suggestions.find((item) => item.id === suggestionId);
      if (suggestion && onScrollToSuggestion) {
        onScrollToSuggestion(suggestion);
      }
    };

    return (
      <DocumentChat
        editor={editor}
        documentId={documentId}
        shareToken={shareToken}
        isVisible={activeView === 'chat'}
        navigateToThreadId={chatNavigateThreadId}
        navigateToMessageId={chatNavigateMessageId}
        focusRequest={chatFocusRequest}
        onNavigationHandled={clearChatNavigation}
        onNavigateToSuggestions={openSuggestions}
        onLoadSuggestions={onLoadSuggestions}
        onHighlightSuggestions={onHighlightSuggestions}
        onScrollToSuggestionById={scrollToSuggestionById}
      />
    );
  };

  const renderTechnicalSheet = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return (
      <TechnicalSheet
        isVisible={activeView === 'technicalSheet'}
        token={token}
        documentId={documentId}
        t={t}
        showErrorToast={showErrorToast}
        isSidebar={true}
      />
    );
  };

  const renderExport = (): React.ReactNode => {
    return (
      <ExportDocument
        editor={editor}
        ydoc={ydoc}
        documentTitle={documentTitle}
        projectTitle={projectTitle}
        documentId={documentId ?? undefined}
        token={token}
        comments={comments}
        suggestions={suggestions}
        t={t}
      />
    );
  };

  const renderShare = (): React.ReactNode => {
    return (
      <ShareDocument
        token={token}
        documentId={documentId ?? undefined}
        projectId={projectId}
        documentProjectId={documentProjectId}
        t={t}
        showToast={showToast}
      />
    );
  };

  const renderVersionHistory = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return (
      <VersionHistory
        documentId={documentId}
        onVersionRestored={() => {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }}
        isVisible={activeView === 'versionHistory'}
      />
    );
  };

  const renderStats = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return (
      <DocumentStats
        documentId={documentId}
        wordCount={wordCount}
        readingTime={readingTime}
        pageCount={pageCount}
        selectedWordCount={selectedWordCount}
        selectedReadingTime={selectedReadingTime}
        selectedPageCount={selectedPageCount}
        t={t}
      />
    );
  };

  const renderSettings = (): React.ReactNode => {
    if (!documentId) {
      return null;
    }
    return (
      <WritingSettings
        documentId={documentId}
        projectId={projectId}
        onUpdate={() => {
          if (onLoadSuggestions) {
            onLoadSuggestions();
          }
        }}
      />
    );
  };

  const sidebar = useRightSidebarShadcn();

  const getDefaultDrawerWidth = (view: typeof activeView): number => {
    if (view === 'chapters' || view === 'analysis' || view === 'storylines' || view === 'scenes' || view === 'characters' || view === 'locations' || view === 'technicalSheet' || view === 'export' || view === 'share' || view === 'versionHistory' || view === 'stats') {
      return Math.max(MIN_DRAWER_WIDTH, Math.min(window.innerWidth * 0.8, MAX_DRAWER_WIDTH));
    }
    return MIN_DRAWER_WIDTH;
  };

  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    if (typeof document === 'undefined') return MIN_DRAWER_WIDTH;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => c.trim().startsWith(`${RIGHT_SIDEBAR_WIDTH_COOKIE_NAME}=`));
    if (cookie) {
      const value = parseInt(cookie.split('=')[1], 10);
      if (!isNaN(value) && value >= MIN_DRAWER_WIDTH && value <= MAX_DRAWER_WIDTH) {
        return value;
      }
    }
    return MIN_DRAWER_WIDTH;
  });

  const [iconRailExpanded, setIconRailExpanded] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => c.trim().startsWith(`${RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME}=`));
    if (cookie) {
      const value = cookie.split('=')[1];
      return value === 'true';
    }
    return false;
  });

  const [isResizing, setIsResizing] = useState<boolean>(false);

  const handleCollapseButtonClick = React.useCallback((): void => {
    if (activeView) {
      collapseSidebar();
      setIconRailExpanded(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME}=false; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    } else if (iconRailExpanded) {
      setIconRailExpanded(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME}=false; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    } else {
      setIconRailExpanded(true);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME}=true; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    }
  }, [activeView, collapseSidebar, iconRailExpanded]);

  useEffect(() => {
    if (!activeView) return;
    const defaultWidth = getDefaultDrawerWidth(activeView);
    const clampedWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, defaultWidth));
    setDrawerWidth(clampedWidth);
  }, [activeView]);

  useEffect(() => {
    if (!activeView || !iconRailExpanded) return;
    setIconRailExpanded(false);
    if (typeof document !== 'undefined') {
      document.cookie = `${RIGHT_SIDEBAR_ICON_EXPANDED_COOKIE_NAME}=false; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
    }
  }, [activeView]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent): void => {
      const iconWidthRem = iconRailExpanded
        ? parseFloat(RIGHT_SIDEBAR_WIDTH_ICON_EXPANDED)
        : parseFloat(RIGHT_SIDEBAR_WIDTH_ICON);
      const iconWidthPx = iconWidthRem * 16;
      const newWidth = window.innerWidth - e.clientX - iconWidthPx;
      const clampedWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, newWidth));
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = (): void => {
      setIsResizing(false);
      if (typeof document !== 'undefined') {
        document.cookie = `${RIGHT_SIDEBAR_WIDTH_COOKIE_NAME}=${drawerWidth}; path=/; max-age=${RIGHT_SIDEBAR_COOKIE_MAX_AGE}`;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, drawerWidth, iconRailExpanded]);

  const handleResizeStart = (e: React.MouseEvent): void => {
    e.preventDefault();
    setIsResizing(true);
  };

  const getSidebarWidth = (): string => {
    return `${drawerWidth}px`;
  };

  const getSidebarWidthMobile = (): string => {
    if (activeView === 'chapters' || activeView === 'analysis' || activeView === 'storylines' || activeView === 'scenes' || activeView === 'characters' || activeView === 'locations' || activeView === 'technicalSheet' || activeView === 'export' || activeView === 'share' || activeView === 'versionHistory') {
      return 'min(85vw, 22rem)';
    }
    return RIGHT_SIDEBAR_WIDTH_MOBILE;
  };

  if (sidebar.isMobile) {
    return (
      <>
        <EditorHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
        <Sheet open={sidebar.openMobile} onOpenChange={sidebar.setOpenMobile}>
          <SheetContent
            side="right"
            className="w-[--sidebar-width] p-0 text-sidebar-foreground [&>button]:hidden"
            style={
              {
                '--sidebar-width': getSidebarWidthMobile(),
                backgroundColor: 'var(--card)',
              } as React.CSSProperties
            }
          >
          <SheetHeader className="sr-only">
            <SheetTitle>{t('right_sidebar')}</SheetTitle>
            <SheetDescription>{t('right_sidebar_description')}</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-3 pb-1 [&_[data-sidebar=menu-button]]:!py-2 [&_[data-sidebar=menu-button]]:!px-3 [&_[data-active=true]]:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:!text-primary [&_[data-active=true]]:hover:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:hover:!text-primary [&_[data-active=true]_svg]:!text-[var(--scribane-green)]">
                <SidebarGroup className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'p-1')}>
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'items-center', sidebar.isMobile && 'items-stretch')}>
                      <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                        <RightSidebarMenuButton
                          onClick={collapseSidebar}
                          tooltip={t('close_drawer')}
                        >
                          <PanelRightClose className="h-5 w-5 text-secondary-foreground" />
                          <span>{t('close_drawer')}</span>
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <Separator className="my-1 bg-sidebar-border" />
                <SidebarGroup className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'p-1')}>
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'items-center', sidebar.isMobile && 'items-stretch')}>
                      <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                        <RightSidebarMenuButton
                          onClick={showSuggestions}
                          tooltip={t('suggestions')}
                          isActive={activeView === 'suggestions' || activeMainAreaView === 'suggestions'}
                        >
                          <Lightbulb className="h-5 w-5 text-secondary-foreground" />
                          <span>{t('suggestions')}</span>
                          <CountBadge count={pendingSuggestionsCount} mode="inline" />
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                      {editor && (
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showChat}
                            tooltip={t('chat_with_document')}
                            isActive={activeView === 'chat' || activeMainAreaView === 'chat'}
                          >
                            <Bot className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('chat_with_document')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                      {documentId && (
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showComments}
                            tooltip={t('comments')}
                            isActive={activeView === 'comments' || activeMainAreaView === 'comments'}
                          >
                            <MessageSquare className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('comments')}</span>
                            <CountBadge count={pendingCommentsCount} mode="inline" />
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <Separator className="my-1 bg-sidebar-border" />
                {documentId && (
                  <>
                  <SidebarGroup className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'p-1', 'mt-2')}>
                    <SidebarGroupContent>
                      <SidebarMenu className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'items-center', sidebar.isMobile && 'items-stretch')}>
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showTechnicalSheet}
                            tooltip={t('technical_sheet')}
                            isActive={activeMainAreaView === 'technicalSheet'}
                          >
                            <FileText className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('technical_sheet')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showExport}
                            tooltip={t('export')}
                            isActive={activeMainAreaView === 'export'}
                          >
                            <Download className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('export')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                        {canShare && (
                          <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                            <RightSidebarMenuButton
                              onClick={showShare}
                              tooltip={t('share_document')}
                              isActive={activeMainAreaView === 'share'}
                            >
                              <ShareIcon className="h-5 w-5 text-secondary-foreground" />
                              <span>{t('share_document')}</span>
                            </RightSidebarMenuButton>
                          </SidebarMenuItem>
                        )}
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showVersionHistory}
                            tooltip={t('versionHistory.title')}
                            isActive={activeMainAreaView === 'versionHistory'}
                          >
                            <VersionHistoryIcon className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('versionHistory.title')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showStats}
                            tooltip={t('documentStats.title')}
                            isActive={activeMainAreaView === 'stats'}
                          >
                            <BarChart3 className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('documentStats.title')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                  </>
                )}
                <Separator className="my-1 bg-sidebar-border" />
                <SidebarGroup className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'p-1', 'mt-2')}>
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'items-center', sidebar.isMobile && 'items-stretch')}>
                      {documentId && (
                        <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                          <RightSidebarMenuButton
                            onClick={showSettings}
                            tooltip={t('document_settings')}
                            isActive={activeMainAreaView === 'settings'}
                          >
                            <Settings className="h-5 w-5 text-secondary-foreground" />
                            <span>{t('settings')}</span>
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                      <SidebarMenuItem className={cn(sidebar.state === 'collapsed' && !sidebar.isMobile && 'flex justify-center', sidebar.isMobile && 'flex justify-start')}>
                        <RightSidebarMenuButton
                          onClick={() => setHelpDialogOpen(true)}
                          tooltip={t('help')}
                        >
                          <HelpCircle className="h-5 w-5 text-secondary-foreground" />
                          <span>{t('help')}</span>
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </div>
              <div className="p-4 pt-1">
                {activeView === 'suggestions' && renderSuggestions()}
                {activeView === 'chat' && renderChat()}
                {activeView === 'comments' && renderComments()}
                {activeView === 'chapters' && renderChapters()}
                {activeView === 'analysis' && renderAnalysis()}
                {activeView === 'storylines' && renderStorylines()}
                {activeView === 'scenes' && renderScenes()}
                {activeView === 'characters' && renderCharacters()}
                {activeView === 'locations' && renderLocations()}
                {activeView === 'technicalSheet' && renderTechnicalSheet()}
                {activeView === 'export' && renderExport()}
                {activeView === 'share' && renderShare()}
                {activeView === 'versionHistory' && renderVersionHistory()}
                {activeView === 'stats' && renderStats()}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      </>
    );
  }

  return (
    <>
      <EditorHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
      <div
        className="group peer hidden text-sidebar-foreground md:block"
        data-state={sidebar.state}
        data-side="right"
      >
      <div
        className={cn(
          'relative bg-transparent transition-[width] duration-200 ease-linear',
        )}
        style={
          {
            width: (() => {
              const iconWidth = iconRailExpanded ? RIGHT_SIDEBAR_WIDTH_ICON_EXPANDED : RIGHT_SIDEBAR_WIDTH_ICON;
              return activeView ? `calc(${iconWidth} + ${drawerWidth}px)` : iconWidth;
            })(),
          } as React.CSSProperties
        }
      />
      <div
        className={cn(
          'fixed hidden md:flex flex-row-reverse',
          'right-0',
          'inset-y-0 h-svh'
        )}
        style={{ zIndex: 1 }}
      >
        <div
          className="flex h-full flex-col shrink-0 border-l border-sidebar-border"
          style={{
            width: iconRailExpanded ? RIGHT_SIDEBAR_WIDTH_ICON_EXPANDED : RIGHT_SIDEBAR_WIDTH_ICON,
            backgroundColor: 'var(--card)',
          }}
        >
          <SidebarHeader className="flex flex-col h-full p-0 gap-0 [&_[data-active=true]]:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:!text-primary [&_[data-active=true]]:hover:!bg-[rgba(94,204,128,0.16)] [&_[data-active=true]]:hover:!text-primary [&_[data-active=true]_svg]:!text-[var(--scribane-green)]">
            <SidebarGroup className="h-16 shrink-0 justify-center border-b border-border p-0">
              <SidebarGroupContent className="px-1">
                <SidebarMenu className={cn(iconRailExpanded ? 'items-stretch' : 'items-center')}>
                  <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                    <RightSidebarMenuButton
                      onClick={handleCollapseButtonClick}
                      tooltip={Boolean(activeView) || iconRailExpanded ? t('close_drawer') : t('open_drawer')}
                      hideTooltip={iconRailExpanded}
                      className={cn(
                        !iconRailExpanded && '!size-8 !p-2 !justify-center !text-center',
                        iconRailExpanded && expandedRailButtonClassName
                      )}
                    >
                      {Boolean(activeView) || iconRailExpanded ? (
                        <PanelRightClose className={cn(iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 shrink-0 text-secondary-foreground')} />
                      ) : (
                        <PanelRightOpen className={cn(iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 shrink-0 text-secondary-foreground')} />
                      )}
                      {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('close_drawer')}</span>}
                    </RightSidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup className="p-0 mt-2">
              <SidebarGroupContent className="px-1">
                <SidebarMenu className={cn('gap-2', iconRailExpanded ? 'items-stretch' : 'items-center')}>
                  <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                    <RightSidebarMenuButton
                      onClick={showSuggestions}
                      tooltip={t('suggestions')}
                      hideTooltip={iconRailExpanded}
                      isActive={activeView === 'suggestions'}
                      className={cn(
                        !iconRailExpanded && '!size-8 !p-2 !justify-center !text-center relative',
                        iconRailExpanded && expandedRailButtonClassName
                      )}
                    >
                      <Lightbulb className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 shrink-0 text-secondary-foreground'} />
                      {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('suggestions')}</span>}
                      <CountBadge
                        count={pendingSuggestionsCount}
                        mode={iconRailExpanded ? 'inline' : 'overlay'}
                      />
                    </RightSidebarMenuButton>
                  </SidebarMenuItem>
                  {editor && (
                    <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                      <RightSidebarMenuButton
                        onClick={showChat}
                        tooltip={t('chat_with_document')}
                        hideTooltip={iconRailExpanded}
                        isActive={activeView === 'chat'}
                        className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                      >
                        <Bot className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                        {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('chat_with_document')}</span>}
                      </RightSidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {documentId && (
                    <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                      <RightSidebarMenuButton
                        onClick={showComments}
                        tooltip={t('comments')}
                        hideTooltip={iconRailExpanded}
                        isActive={activeView === 'comments'}
                        className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center relative', iconRailExpanded && expandedRailButtonClassName)}
                      >
                        <MessageSquare className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                        {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('comments')}</span>}
                        <CountBadge
                          count={pendingCommentsCount}
                          mode={iconRailExpanded ? 'inline' : 'overlay'}
                        />
                      </RightSidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {documentId && (
              <>
                <SidebarGroup className="mt-auto p-0">
                  <SidebarGroupContent className="px-1">
                    <SidebarMenu className={cn('gap-2', iconRailExpanded ? 'items-stretch' : 'items-center')}>
                      <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                        <RightSidebarMenuButton
                          onClick={showTechnicalSheet}
                          tooltip={t('technical_sheet')}
                          hideTooltip={iconRailExpanded}
                          isActive={activeMainAreaView === 'technicalSheet'}
                          className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                        >
                          <FileText className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                          {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('technical_sheet')}</span>}
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                        <RightSidebarMenuButton
                          onClick={showExport}
                          tooltip={t('export')}
                          hideTooltip={iconRailExpanded}
                          isActive={activeMainAreaView === 'export'}
                          className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                        >
                          <Download className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                          {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('export')}</span>}
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                      {canShare && (
                        <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                          <RightSidebarMenuButton
                            onClick={showShare}
                            tooltip={t('share_document')}
                            hideTooltip={iconRailExpanded}
                            isActive={activeMainAreaView === 'share'}
                            className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                          >
                            <ShareIcon className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                            {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('share_document')}</span>}
                          </RightSidebarMenuButton>
                        </SidebarMenuItem>
                      )}
                      <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                        <RightSidebarMenuButton
                          onClick={showVersionHistory}
                          tooltip={t('versionHistory.title')}
                          hideTooltip={iconRailExpanded}
                          isActive={activeMainAreaView === 'versionHistory'}
                          className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                        >
                          <VersionHistoryIcon className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                          {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('versionHistory.title')}</span>}
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                        <RightSidebarMenuButton
                          onClick={showStats}
                          tooltip={t('documentStats.title')}
                          hideTooltip={iconRailExpanded}
                          isActive={activeMainAreaView === 'stats'}
                          className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                        >
                          <BarChart3 className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                          {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('documentStats.title')}</span>}
                        </RightSidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                <Separator className="my-2 bg-sidebar-border" />
              </>
            )}
            <SidebarGroup className={cn('p-0', !documentId && 'mt-auto')}>
              <SidebarGroupContent className="px-1">
                <SidebarMenu className={cn('gap-2', iconRailExpanded ? 'items-stretch' : 'items-center')}>
                  {documentId && (
                    <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                      <RightSidebarMenuButton
                        onClick={showSettings}
                        tooltip={t('document_settings')}
                        hideTooltip={iconRailExpanded}
                        isActive={activeMainAreaView === 'settings'}
                        className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                      >
                        <Settings className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                        {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('document_settings')}</span>}
                      </RightSidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem className={cn(iconRailExpanded ? 'flex justify-start' : 'flex justify-center')}>
                    <RightSidebarMenuButton
                      onClick={() => setHelpDialogOpen(true)}
                      tooltip={t('help')}
                      hideTooltip={iconRailExpanded}
                      className={cn(!iconRailExpanded && '!size-8 !p-2 !justify-center !text-center', iconRailExpanded && expandedRailButtonClassName)}
                    >
                      <HelpCircle className={iconRailExpanded ? expandedRailIconClassName : 'h-5 w-5 text-secondary-foreground'} />
                      {iconRailExpanded && <span className={expandedRailLabelClassName}>{t('help')}</span>}
                    </RightSidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarHeader>
        </div>
        <div
          className={cn(
            "flex h-full flex-col overflow-hidden relative transition-all duration-300 ease-in-out",
            activeView ? "opacity-100 border-l border-sidebar-border" : "opacity-0 pointer-events-none"
          )}
          style={{
            width: activeView ? getSidebarWidth() : '0px',
            borderRight: activeView ? '1px solid hsl(var(--sidebar-border))' : 'none',
            transform: activeView ? 'translateX(0)' : 'translateX(100%)',
            backgroundColor: 'var(--card)',
          }}
        >
          {activeView && (
            <>
              <div
                className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-10 flex items-center justify-center group"
                onMouseDown={handleResizeStart}
                style={{
                  cursor: isResizing ? 'col-resize' : 'col-resize',
                }}
              >
                <div className="flex flex-col items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="h-4 w-4 text-foreground opacity-40" />
                  <GripVertical className="h-4 w-4 text-foreground opacity-40" />
                  <GripVertical className="h-4 w-4 text-foreground opacity-40" />
                </div>
                <div className="absolute inset-0 bg-transparent group-hover:bg-sidebar-accent/30 transition-colors" />
              </div>
              <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
                <SidebarContent className="pt-4 pb-4 pl-4">
                  {activeView === 'suggestions' && renderSuggestions()}
                  {activeView === 'chat' && renderChat()}
                  {activeView === 'comments' && renderComments()}
                  {activeView === 'chapters' && renderChapters()}
                  {activeView === 'analysis' && renderAnalysis()}
                  {activeView === 'storylines' && renderStorylines()}
                  {activeView === 'scenes' && renderScenes()}
                  {activeView === 'characters' && renderCharacters()}
                  {activeView === 'locations' && renderLocations()}
                  {activeView === 'technicalSheet' && renderTechnicalSheet()}
                  {activeView === 'share' && renderShare()}
                  {activeView === 'export' && renderExport()}
                  {activeView === 'versionHistory' && renderVersionHistory()}
                  {activeView === 'stats' && renderStats()}
                </SidebarContent>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export const EditorRightSidebarTrigger: React.FC<React.ComponentProps<typeof Button>> = ({ className, ...props }) => {
  const { t } = useLocale();
  const { toggleSidebar } = useRightSidebar();

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-10 w-10', className)}
      onClick={toggleSidebar}
      aria-label={t('right_sidebar')}
      {...props}
    >
      <PanelRightOpen className="h-10 w-10 text-foreground" />
    </Button>
  );
};

export const EditorRightSidebarSuggestionsTrigger: React.FC<React.ComponentProps<typeof Button>> = ({ className, ...props }) => {
  const { t } = useLocale();
  const { showSuggestions } = useRightSidebar();

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-10 w-10', className)}
      onClick={showSuggestions}
      aria-label={t('show_suggestions')}
      {...props}
    >
      <Lightbulb className="h-5 w-5 text-foreground" />
    </Button>
  );
};

